import { FC, ReactNode, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Divider,
  IconButton,
  Paper,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material';

// Reusable section card component
export const SectionCard: FC<{
  'data-testid'?: string;
  header: ReactNode;
  children: ReactNode;
  sx?: SxProps<Theme>;
  childContainerSx?: SxProps<Theme>;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  showContentAsHeader?: boolean;
}> = ({
  'data-testid': dataTestId,
  header,
  children,
  sx,
  childContainerSx,
  collapsible = false,
  defaultExpanded = true,
  showContentAsHeader = false,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <Paper
      data-testid={dataTestId}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderTop: `4px solid ${theme.palette.divider}`,
        borderRadius: '0px',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.palette.grey[600]
            : theme.palette.common.white,
        boxShadow: 'none',
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '56px',
          px: '24px',
        }}
      >
        {collapsible && (
          <IconButton
            onClick={handleToggle}
            size='small'
            sx={{
              ml: -3,
              mr: -1,
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            aria-label={expanded ? 'Collapse section' : 'Expand section'}
          >
            <ArrowDropDownIcon
              sx={{
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease-in-out',
                fontSize: '50px',
              }}
            />
          </IconButton>
        )}
        {showContentAsHeader ? children : header}
      </Box>
      {expanded && !showContentAsHeader && (
        <>
          <Divider />
          <Box
            sx={[
              {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                p: '20px',
              },
              ...(Array.isArray(childContainerSx)
                ? childContainerSx
                : [childContainerSx]),
            ]}
          >
            {children}
          </Box>
        </>
      )}
    </Paper>
  );
};
